'use client'

import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import {
  CasePrescription,
  FlowBadge,
  PendingRequestCard,
  SERVICE_TYPE_TO_FLOW,
  toMockCaseRecord,
  toMockSteps,
  useCaseDetail,
  useCasePrescriptionSteps,
  useSetMyPrescriptionStepDone,
  type FlowType,
} from '@/features/cases'
import { useMyInvestorCases, type InvestorCase } from '@/features/investors'
import { InvestorShell } from '@/features/investor/components/investor-shell'

// Portfolio (audit) first, then Trades — the order the investor actually cares about identifying
// "this prescription is for what" fastest. Short labels (not `FLOW_LABEL`'s "Portfolio Audit") to
// match the tab style already used for the dashboard's own Portfolio/Trades tabs. 2-flow scope —
// no Goals tab (see PROGRESS.md).
const FLOW_TAB_ORDER: ('audit' | 'trades')[] = ['audit', 'trades']
const FLOW_TAB_LABEL: Record<'audit' | 'trades', string> = {
  audit: 'Portfolio',
  trades: 'Trades',
}

function caseFlow(c: InvestorCase): FlowType {
  return SERVICE_TYPE_TO_FLOW[c.serviceType]
}

/** One card per owned case — its own component so each can call `useCaseDetail`/
 * `useCasePrescriptionSteps` for just its own case id (hooks can't be called in a `.map` loop). */
function CasePrescriptionCard({ summary, flow }: { summary: InvestorCase; flow: FlowType | null }) {
  // A not-yet-published case 404s on `GET /cases/:id` by design (`assertCaseAccess`'s investor
  // branch) — only fetch detail once it's actually unlocked, same reasoning as `useMyCaseForFlow`.
  const isPublished = summary.caseStatus === 'published'
  const caseQuery = useCaseDetail(isPublished ? summary.id : undefined)
  const stepsQuery = useCasePrescriptionSteps(isPublished ? summary.id : undefined)
  const setStepDone = useSetMyPrescriptionStepDone(summary.id)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base font-medium">
          {flow && <FlowBadge flow={flow} />}
          {summary.reference}
        </CardTitle>
        {summary.advisorName ? (
          <Badge variant="secondary" className="max-w-56 truncate">
            {summary.advisorName} is looking after this
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {!isPublished ? (
          <PendingRequestCard
            caseRecord={{
              id: summary.id,
              flow: flow ?? 'audit',
              reference: summary.reference,
              status: summary.caseStatus,
            }}
          />
        ) : caseQuery.isError ? (
          <QueryErrorState error={caseQuery.error} onRetry={() => void caseQuery.refetch()} />
        ) : caseQuery.isLoading || !caseQuery.data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <CasePrescription
            caseRecord={toMockCaseRecord(caseQuery.data)}
            injectedSteps={stepsQuery.data ? toMockSteps(stepsQuery.data) : []}
            onToggleStepDone={(id, done) => setStepDone.mutate({ stepId: id, done })}
          />
        )}
        <div className="text-muted-foreground border-t pt-4 text-xs leading-relaxed">
          Your CAS/tradebook and any reports your advisor shares are sent to you directly — they
          don&apos;t appear in a list here yet.
        </div>
      </CardContent>
    </Card>
  )
}

/** Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardAudit.tsx`. */
export function InvestorAuditPrescriptionView() {
  const { data: allInvestorCases = [] } = useMyInvestorCases()
  const [filter, setFilter] = useState<FlowType | 'all'>('all')

  // This page is scoped to Audit/Trades only — Build has its own dedicated `/dashboard/build`
  // Goals view (see `investorBuildGoalsView.tsx`). Exclude it here so it can't leak into the
  // "All" filter and render via `CasePrescription` (a step-list UI Build cases don't populate).
  const investorCases = allInvestorCases.filter((c) => caseFlow(c) !== 'build')

  // Only offer a tab for a service this investor actually has a case for — no point showing a
  // tab that would just render nothing.
  const presentFlows = FLOW_TAB_ORDER.filter((flow) =>
    investorCases.some((c) => caseFlow(c) === flow),
  )
  const filteredCases =
    filter === 'all' ? investorCases : investorCases.filter((c) => caseFlow(c) === filter)

  if (investorCases.length === 0) {
    return (
      <InvestorShell
        active="audit"
        title="Audit & Prescription"
        description="Your advisor's review and what to do about it, once it's ready."
      >
        <Card>
          <CardContent className="text-muted-foreground py-8 text-center text-sm">
            Nothing to show yet — start a service from the dashboard.
          </CardContent>
        </Card>
      </InvestorShell>
    )
  }

  return (
    <InvestorShell
      active="audit"
      title="Audit & Prescription"
      description="Your advisor's review and what to do about it, once it's ready."
    >
      {presentFlows.length > 1 && (
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FlowType | 'all')}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {presentFlows.map((flow) => (
              <TabsTrigger key={flow} value={flow}>
                {FLOW_TAB_LABEL[flow]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {filteredCases.map((c) => (
        <CasePrescriptionCard key={c.id} summary={c} flow={caseFlow(c)} />
      ))}
    </InvestorShell>
  )
}
